/**
 * SavedResearchList — displays past research sessions with "What Changed" badges.
 */
import { useState, useEffect } from 'react';
import * as api from '../lib/api';
import { useFocal } from '../lib/focal';

interface Session {
  id: string;
  query: string;
  depth: number;
  status: string;
  created_at: string;
  // v0.5.5 AE-080: when a research session was used to publish a thesis
  // (via Buy SIM / Buy LIVE), the backend stores the resulting thesis_id
  // so we can render a "→ thesis" link in this row.
  thesis_id?: string | null;
}

interface Props {
  onLoad: (session: any) => void;
  onModify: (session: Session) => void;
}

export function SavedResearchList({ onLoad, onModify }: Props) {
  const focal = useFocal();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [changes, setChanges] = useState<Record<string, any[]>>({});

  useEffect(() => {
    api.research.history()
      .then((data) => setSessions(data.sessions ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function loadWithChanges(session: Session) {
    const full = await api.research.historyGet(session.id) as any;
    onLoad(full);

    const enrichment = full?.enrichment || full?.result?.enrichment;
    const candidates = enrichment?.candidates || [];
    const savedPrices: Record<string, number> = {};
    for (const c of candidates) {
      if (c.symbol && c.last_price) savedPrices[c.symbol] = c.last_price;
    }

    if (Object.keys(savedPrices).length > 0) {
      try {
        const result = await api.research.whatChanged(Object.keys(savedPrices), savedPrices);
        setChanges((prev) => ({ ...prev, [session.id]: result.changes }));
      } catch {}
    }
  }

  if (loading) return null;
  if (sessions.length === 0) return null;

  return (
    <div className="card p-3">
      <button
        className="text-[11px] text-ink-mute uppercase w-full text-left flex justify-between items-center"
        onClick={() => setExpanded(!expanded)}
      >
        <span>saved research ({sessions.length})</span>
        <span>{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div className="mt-2 space-y-1 max-h-64 overflow-auto">
          {sessions.map((s) => {
            const sessionChanges = changes[s.id] || [];
            const notable = sessionChanges.filter((c) => c.notable);
            return (
              <div key={s.id} className="border-t border-edge py-1.5">
                <div className="flex items-center gap-2 text-xs">
                  <button
                    className="text-accent hover:underline flex-1 text-left truncate"
                    onClick={() => loadWithChanges(s)}
                  >
                    {s.query}
                  </button>
                  {notable.length > 0 && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">
                      {notable.length} changed
                    </span>
                  )}
                  <span className="text-[10px] text-ink-mute">
                    {new Date(s.created_at).toLocaleDateString()}
                  </span>
                  <button
                    className="text-[10px] text-accent hover:underline"
                    onClick={() => onModify(s)}
                  >
                    modify
                  </button>
                  {s.thesis_id && (
                    <button
                      className="text-[10px] text-emerald-700 hover:underline"
                      onClick={() => focal.set({ kind: 'thesis_detail', thesisId: s.thesis_id! })}
                      data-testid={`saved-research-thesis-link-${s.id}`}
                      title="Open the thesis this research published"
                    >
                      → thesis
                    </button>
                  )}
                </div>
                {sessionChanges.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {sessionChanges.filter(c => c.price_change_pct != null).map((c) => (
                      <span
                        key={c.symbol}
                        className={`text-[10px] px-1 py-0.5 rounded ${
                          c.price_change_pct > 5 ? 'bg-emerald-50 text-emerald-700' :
                          c.price_change_pct < -5 ? 'bg-red-50 text-red-700' :
                          'bg-slate-50 text-slate-600'
                        }`}
                      >
                        {c.symbol} {c.price_change_pct > 0 ? '+' : ''}{c.price_change_pct?.toFixed(1)}%
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
